import { ObservableQuery, ObservableSubscription } from "@apollo/client";
import { computed, observable, runInAction } from "mobx";
import { fromResource, IResource } from "mobx-utils";
import { isEmpty } from "lodash";
import { SimpleEventEmitter } from "@utils/SimpleEventEmitter";

type ObservableQueryOptions = {
  lazy?: boolean;
  onError?: (err: any) => void; // fix any type
};

export class ObservableGraphqlQuery<Variables, Return> {
  @observable loading = true;
  @observable error: string | null = null;
  onFetch: SimpleEventEmitter = new SimpleEventEmitter();

  private query: ObservableQuery<Return, Variables>;
  private subscription: ObservableSubscription;
  private resource: IResource<Return>;

  constructor(
    private createQuery: () => ObservableQuery<Return, Variables>,
    private options: ObservableQueryOptions = {}
  ) {
    if (!options.lazy) {
      this.createResourceFromSubscription();
    }
  }

  @computed get data() {
    return this.resource ? this.resource.current() : null;
  }

  refetchAsync = (variables?: Variables) => {
    return this.query
      .refetch(variables)
      .catch((err) => this.options.onError && this.options.onError(err));
  };

  refetch = (variables?: Variables) => {
    this.refetchAsync(variables);
  };

  private createResourceFromSubscription = () => {
    this.resource = fromResource(
      (sink) => {
        this.query = this.createQuery();
        this.subscription = this.query.subscribe({
          next: (value) => {
            runInAction(() => {
              this.loading = value.loading;
              this.error = !isEmpty(value.errors) ? value.errors.toString() : null;

              // trigger onError
              this.error && this.options.onError && this.options.onError(this.error);

              if (value.loading || value.errors) {
                return;
              }

              sink(value.data);
              this.onFetch.trigger();
            });
          },
          error: (err) => {
            runInAction(() => {
              this.loading = false;
              this.error = err?.message ? String(err.message) : String(err);
            });
            console.error("[ObservableGraphqlQuery] subscription error", err);
            this.options.onError && this.options.onError(err);
          }
        });
      },
      () => {
        this.query && this.query.stopPolling();
        this.subscription && this.subscription.unsubscribe();
      }
    );
  };
}
